// src/pages/Home.jsx
import React, { useState } from "react";

const progressMetrics = [
  {
    label: "Listings mirrored",
    value: "1,240",
    meta: "Across three marketplaces",
  },
  {
    label: "Daily sync operations",
    value: "4,800+",
    meta: "Automatically queued for partners",
  },
  {
    label: "Partner seats active",
    value: "5",
    meta: "More invitations rolling out this quarter",
  },
];

const milestones = [
  {
    title: "Internal preview launch",
    timeframe: "Jan 2025",
    description:
      "Rolled out Reflectify to DPA's internal teams for usability and performance tuning.",
    status: "Complete",
    tone: "complete",
  },
  {
    title: "Partner onboarding cohort",
    timeframe: "Ongoing",
    description:
      "Working directly with sellers to synchronise listings and tune their branded storefronts.",
    status: "In progress",
    tone: "progress",
  },
  {
    title: "Public documentation",
    timeframe: "Q4 2025",
    description:
      "Shipping demos, operating playbooks, and marketplace integration guides before wider launch.",
    status: "Scheduled",
    tone: "planned",
  },
];

const liveUpdates = [
  {
    title: "Adaptive layouts now live",
    body: "The storefront blends with antiques, electronics, and craft categories without manual themes.",
    tag: "Product work",
    time: "2 days ago",
  },
  {
    title: "Sync observability dashboard",
    body: "Added telemetry so partners can see their refresh cadence, success rate, and backlog in real time.",
    tag: "Platform",
    time: "Last week",
  },
  {
    title: "Seller playbook updated",
    body: "The new onboarding guide includes readiness checklists, privacy notes, and a set of branded assets.",
    tag: "Enablement",
    time: "Last week",
  },
];

function Home() {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [newsletterFeedback, setNewsletterFeedback] = useState("");

  const handleNewsletterSubmit = (event) => {
    event.preventDefault();
    if (email.trim() && email.includes("@")) {
      setNewsletterFeedback("Thanks! We'll be in touch with early previews.");
      setEmail("");
    } else {
      setNewsletterFeedback("Please share a valid email address we can reach you on.");
    }
  };

  return (
    <div className="site">
      <header className="site-header">
        <div className="site-header__inner">
          <div className="site-header__brand">
            <span className="site-header__logo-dot" />
            <span className="site-header__logo-text">Reflectify</span>
          </div>
          <nav className="site-header__nav">
            <a href="#features">Features</a>
            <a href="#how-it-works">How it works</a>
            <a href="#who-its-for">Who it’s for</a>
            <a href="#about-dpa">About DPA</a>
            <a href="#progress">Status</a>
          </nav>
        </div>
      </header>

      <main className="site-main">
        {/* Hero */}
        <section className="hero">
          <div className="hero__inner">
            <div className="hero__content">
              <h1 className="hero__title">Reflectify</h1>
              <p className="hero__subtitle">
                A next-generation storefront mirroring platform for independent sellers.
              </p>
              <p className="hero__body">
                Reflectify synchronises your marketplace listings and transforms them into a
                beautiful, modern web storefront — effortlessly. You keep the payment flows,
                reviews, and protections of platforms like eBay, while Reflectify gives you
                a clean, branded shop of your own.
              </p>

              <div className="hero__cta-row">
                <span className="hero__pill">Under active development</span>
                <span className="hero__pill hero__pill--outline">
                  Internal preview · Partner onboarding phase
                </span>
              </div>
            </div>

            <aside className="hero__card" aria-label="Reflectify status">
              <h2 className="hero__card-title">Official Reflectify Website</h2>
              <p className="hero__card-body">
                This is the official public home of <strong>Reflectify</strong>, a product
                currently under active development and commercial deployment by{" "}
                <strong>DPA Cloud Services Ltd</strong>.
              </p>
              <p className="hero__card-body">
                The full Reflectify experience will launch here following internal preview
                and partner onboarding. Early access is being coordinated directly with
                selected sellers and partners.
              </p>
              <p className="hero__card-note">
                Reflectify™ — A DPA Cloud Services Product.
              </p>
            </aside>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="section">
          <div className="section__inner">
            <h2 className="section__title">What Reflectify does</h2>
            <p className="section__lead">
              Reflectify connects to your existing marketplace accounts and builds a
              storefront that reflects your live listings — no double entry, no reinventing
              your business.
            </p>

            <div className="feature-grid">
              <div className="feature-card">
                <h3 className="feature-card__title">Mirror, don’t migrate</h3>
                <p className="feature-card__body">
                  Keep listing on your preferred marketplaces. Reflectify reads your public
                  listings and mirrors them into a unified storefront that looks and feels
                  like <em>your</em> brand.
                </p>
              </div>
              <div className="feature-card">
                <h3 className="feature-card__title">Click-through checkouts</h3>
                <p className="feature-card__body">
                  Customers browse on your Reflectify site, then complete purchases on the
                  underlying marketplace. That means the marketplace still handles payments,
                  disputes, and buyer protections — you avoid additional PCI scope.
                </p>
              </div>
              <div className="feature-card">
                <h3 className="feature-card__title">Simple, branded presentation</h3>
                <p className="feature-card__body">
                  Choose from curated, theme-based layouts tailored to antiques, tech,
                  collectables, and more. Your products stay the same, but the browsing
                  experience becomes calmer, cleaner, and easier to share.
                </p>
              </div>
              <div className="feature-card">
                <h3 className="feature-card__title">Low maintenance by design</h3>
                <p className="feature-card__body">
                  Reflectify periodically synchronises your listings on a defined schedule.
                  You keep working in the tools you know; Reflectify quietly keeps your
                  storefront up to date.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="section section--alt">
          <div className="section__inner">
            <h2 className="section__title">How Reflectify works</h2>
            <div className="how-grid">
              <div className="how-step">
                <span className="how-step__badge">Step 1</span>
                <h3 className="how-step__title">Connect your marketplace accounts</h3>
                <p className="how-step__body">
                  You connect Reflectify to your existing seller accounts (for example,
                  eBay). We read your public listings and metadata using official or
                  standards-based integrations.
                </p>
              </div>
              <div className="how-step">
                <span className="how-step__badge">Step 2</span>
                <h3 className="how-step__title">Reflect your catalogue</h3>
                <p className="how-step__body">
                  Reflectify builds a searchable, shareable storefront using your live
                  listings. Categories, filters, and product cards are generated
                  automatically.
                </p>
              </div>
              <div className="how-step">
                <span className="how-step__badge">Step 3</span>
                <h3 className="how-step__title">Send buyers to their comfort zone</h3>
                <p className="how-step__body">
                  When someone clicks &quot;View on marketplace&quot;, they are taken
                  straight to the listing on the original platform to complete their
                  purchase — keeping your compliance profile simple and familiar.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Who it's for */}
        <section id="who-its-for" className="section">
          <div className="section__inner">
            <h2 className="section__title">Who Reflectify is for</h2>
            <div className="who-grid">
              <div className="who-card">
                <h3 className="who-card__title">Independent sellers</h3>
                <p className="who-card__body">
                  You sell through marketplaces and want a link you can confidently share
                  on social media, in messages, or on business cards without sending people
                  into a noisy search page.
                </p>
              </div>
              <div className="who-card">
                <h3 className="who-card__title">Small shops &amp; side businesses</h3>
                <p className="who-card__body">
                  You would like a branded online presence, but you do not want to manage a
                  second inventory system or take on full e-commerce responsibilities just
                  yet.
                </p>
              </div>
              <div className="who-card">
                <h3 className="who-card__title">Partners &amp; agencies</h3>
                <p className="who-card__body">
                  You support multiple sellers and want a repeatable way to give each of
                  them a clean storefront that still respects their existing marketplace
                  workflows.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* About DPA */}
        <section id="about-dpa" className="section section--alt">
          <div className="section__inner">
            <h2 className="section__title">Built by DPA Cloud Services Ltd</h2>
            <p className="section__lead">
              Reflectify is designed and operated by DPA Cloud Services Ltd, a UK-based
              provider focused on secure, well-engineered infrastructure and practical,
              real-world automation.
            </p>
            <p className="section__body">
              Reflectify sits alongside DPA&apos;s existing cloud and software offerings,
              and is being developed with the same focus on reliability, clarity, and
              long-term maintainability.
            </p>
          </div>
        </section>

        <section id="progress" className="section section--progress">
          <div className="section__inner">
            <div className="progress-header">
              <h2 className="section__title">Work in progress</h2>
              <p className="section__lead">
                This board highlights what the Reflectify team is shipping now, so partners know
                what to expect during the internal preview phase.
              </p>
            </div>

            <div className="status-grid">
              {progressMetrics.map((metric) => (
                <article className="metric-card" key={metric.label}>
                  <p className="metric-card__value">{metric.value}</p>
                  <p className="metric-card__label">{metric.label}</p>
                  <p className="metric-card__meta">{metric.meta}</p>
                </article>
              ))}
            </div>

            <div className="milestone-board">
              <div className="milestone-board__header">
                <h3>Recent milestones</h3>
                <p>
                  Every milestone is tracked with measurable checkpoints before the next release window.
                </p>
              </div>
              <div className="milestone-grid">
                {milestones.map((item) => (
                  <article className="milestone-card" key={item.title}>
                    <div className="milestone-card__header">
                      <p
                        className={`milestone-card__status milestone-card__status--${item.tone}`}
                      >
                        {item.status}
                      </p>
                      <span className="milestone-card__time">{item.timeframe}</span>
                    </div>
                    <h4 className="milestone-card__title">{item.title}</h4>
                    <p className="milestone-card__body">{item.description}</p>
                  </article>
                ))}
              </div>
            </div>

            <div className="updates-grid">
              <article className="updates-card">
                <div className="updates-card__header">
                  <h3>Latest work</h3>
                  <p>Small, iterative improvements arrive every sprint.</p>
                </div>
                <ul className="updates-list">
                  {liveUpdates.map((update) => (
                    <li className="updates-list__item" key={update.title}>
                      <div>
                        <p className="updates-list__tag">{update.tag}</p>
                        <h4 className="updates-list__title">{update.title}</h4>
                        <p className="updates-list__body">{update.body}</p>
                      </div>
                      <span className="updates-list__time">{update.time}</span>
                    </li>
                  ))}
                </ul>
              </article>
              <article className="newsletter-card">
                <h3>Partner updates</h3>
                <p>
                  Drop your email and we will share progress notes, release announcements, and
                  partner-only previews as they happen.
                </p>
                <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                  <label className="sr-only" htmlFor="newsletter-email">
                    Email address
                  </label>
                  <input
                    id="newsletter-email"
                    type="email"
                    name="newsletter-email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <button type="submit">Join the waitlist</button>
                </form>
                {newsletterFeedback && (
                  <p className="newsletter-feedback" aria-live="polite">
                    {newsletterFeedback}
                  </p>
                )}
              </article>
            </div>
          </div>
        </section>

        {/* Early access / status */}
        <section className="section section--status">
          <div className="section__inner section__inner--center">
            <h2 className="section__title">Current status</h2>
            <p className="section__lead">
              Reflectify is in an internal preview and early partner onboarding phase.
              Wider access, public demos, and technical documentation will be published
              from this site as the platform matures.
            </p>
            <p className="section__body">
              Until then, this page serves as the official public home of the Reflectify
              brand and service.
            </p>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="site-footer__inner">
          <p>© {year} DPA Cloud Services Ltd</p>
          <p>Reflectify™ — A DPA Cloud Services Product</p>
          <p>All rights reserved.</p>
          <p className="site-footer__legal">
            This page constitutes public evidence of brand usage, commercial intent, and
            product identification under UK jurisdiction.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Home;
